import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useEnableFundraising -- shared read of the SuperAdmin
 * ``enable_fundraising`` platform-settings switch (Trello #623).
 *
 * Fundraiser / Donations is hidden platform-wide until an SA turns this on.
 * It is a SOFT hide: a store's own ``fundraising_*`` settings are kept, and
 * the server already neutralizes fundraising in pricing and in the shopper
 * store reads. This composable gates the back-office surfaces the frontend
 * owns: the store editor's Fundraising section and the order views.
 *
 * Fail-CLOSED default FALSE -- the OPPOSITE of useEnableDepartments. Hiding
 * is the safe direction here: a transient read failure must never show a
 * donation line the server will not charge. Any non-boolean answer or a
 * failed read resolves to FALSE.
 *
 *   const { enableFundraising, loading, error } = useEnableFundraising()
 *   enableFundraising: Ref<boolean>   // TRUE = show Fundraising surfaces
 */
const ENDPOINT = '/api/platform-settings/enable-fundraising'

// Module-level shared state: one fetch per page load, shared by every caller.
const _value = ref(false)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) {
        return Promise.resolve(_value.value)
    }
    if (_inflight) {
        return _inflight
    }
    _loading.value = true
    _error.value = null
    _inflight = axiosInstance
        .get(ENDPOINT)
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean can turn the surfaces ON.
            _value.value = raw === true
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail CLOSED; leave unresolved so a later mount can retry.
            _error.value = err
            _value.value = false
            return _value.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/** Test-only: reset the module-level shared state. */
export function _resetEnableFundraisingCache() {
    _value.value = false
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useEnableFundraising() {
    _fetchOnce()
    return {
        enableFundraising: _value,
        loading: _loading,
        error: _error,
    }
}

export default useEnableFundraising
